/**
 * ListHeader Component
 *
 * Renders column headers for list display with optional sorting support.
 * Automatically hidden on mobile devices.
 *
 * @module components/ui/PaginatedList/components/ListHeader
 */

import { memo, useCallback, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { IconChevronUp, IconChevronDown, IconSelector } from '@tabler/icons-react';
import clsx from 'clsx';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import type { ColumnHeaderConfig, SortConfig } from '../types';
import styles from './ListHeader.module.css';

interface ListHeaderProps {
  /** Column configurations */
  readonly columns: readonly ColumnHeaderConfig[];
  /** Additional class name */
  readonly className?: string;
  /** Current sort configuration */
  readonly sortConfig?: SortConfig;
  /** Callback when sort changes */
  readonly onSortChange?: (config: SortConfig) => void;
}

function ListHeaderInner({
  columns,
  className,
  sortConfig,
  onSortChange,
}: ListHeaderProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();

  const handleColumnClick = useCallback(
    (sortKey: string) => {
      if (!onSortChange) return;

      const newDirection =
        sortConfig?.key === sortKey && sortConfig.direction === 'asc'
          ? 'desc'
          : 'asc';

      onSortChange({ key: sortKey, direction: newDirection });
    },
    [sortConfig, onSortChange]
  );

  // Hide on mobile - return null for performance
  if (isMobile) {
    return null;
  }

  const renderSortIcon = (sortKey: string) => {
    const isActive = sortConfig?.key === sortKey;
    const iconSize = 14;

    if (!isActive) {
      return <IconSelector size={iconSize} className={styles.sortIcon} />;
    }

    return sortConfig.direction === 'asc' ? (
      <IconChevronUp size={iconSize} className={styles.sortIcon} />
    ) : (
      <IconChevronDown size={iconSize} className={styles.sortIcon} />
    );
  };

  return (
    <div
      role="row"
      className={clsx(styles.header, className)}
    >
      {columns.map(column => {
        const { sortKey } = column;
        const isSortable = sortKey != null && onSortChange != null;
        const isActiveSort = sortConfig?.key === sortKey;

        return (
          <div
            key={column.key}
            role="columnheader"
            data-flex={column.width === '1fr' ? true : undefined}
            aria-sort={
              isActiveSort && sortConfig
                ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending')
                : undefined
            }
            className={clsx(
              styles.column,
              column.align === 'center' && styles.alignCenter,
              column.align === 'right' && styles.alignRight,
              (!column.align || column.align === 'left') && styles.alignLeft,
              isSortable && styles.sortable,
              isActiveSort && styles.sortActive
            )}
            style={
              column.width !== '1fr'
                ? { '--col-width': column.width } as CSSProperties
                : undefined
            }
            onClick={isSortable ? () => handleColumnClick(sortKey) : undefined}
            onKeyDown={
              isSortable
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleColumnClick(sortKey);
                    }
                  }
                : undefined
            }
            tabIndex={isSortable ? 0 : undefined}
          >
            {column.label ? t(column.label) : null}
            {isSortable && renderSortIcon(sortKey)}
          </div>
        );
      })}
    </div>
  );
}

export const ListHeader = memo(ListHeaderInner);

ListHeader.displayName = 'ListHeader';
